// "Create export batch" — ONE ACTIONS entry for every source module's list
// (Aaron 2026-09-30: "pickers are action menus in the module · multi tick and
// create batch"). Legacy's popup picker (plugins/<Type>/myob_import_tab_list)
// becomes this: tick records in their own list, choose the action, and the
// batch is created from them (POST /v6/batchexports/from-records, BP-20 —
// collect, then write). A source module adds ONE line:
//
//   const createExportBatch = useCreateExportBatchAction(EXPORT_TYPE.INVOICES, "invoice");
//   ...if (createExportBatch) actions.push(createExportBatch);
//
// The entry is absent — not disabled — for a user without the create grant
// (BP-73: no dead affordance). The outcome is reported here, in the module's
// own words: what was batched, and every ticked record left out with its reason.
//
// A list whose records can be ineligible passes an `eligibility` (H281): when
// NONE of the ticked rows can export, the entry greys out with the reason as its
// secondary line (Pattern 340 §2b), instead of letting the click fail. Legacy's
// picker only offered a checkbox on an eligible record. A mixed selection still
// runs: the backend batches the eligible ones and the rest are named as left out.
import { useMemo } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import type { ActionMenuItem } from "../components/datagrid/ActionMenu";

import { createBatchExportFromRecords, NothingToExportError, type ExportBatchRefusal } from "./batchexports";
import { useBatchExportGrants } from "./grants";

/** The export types a list can create a batch from (legacy myob_import_types). */
export const EXPORT_TYPE = {
  INVOICES: 1,
  ORDERS: 2,
  TDS: 3,
  CUSTOMERS: 4,
  ITEM_ADJUSTMENTS: 7,
} as const;

/** Which ticked rows can go in a batch — the backend's collector for the type,
 * read from the same config — and what to tell the user when none can. */
export interface ExportEligibility {
  isEligible: (row: any) => boolean;
  reason: string;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** "#12 already exported; #14 status is WIP …" — the first few, then a count. */
export function describeRefusals(refused: ExportBatchRefusal[], max = 3): string {
  const shown = refused.slice(0, max).map((r) => `#${r.id} ${r.reason}`);
  const more = refused.length - shown.length;
  return shown.join("; ") + (more > 0 ? `; and ${more} more` : "");
}

function idsOf(payload: any): number[] {
  const rows = payload?.selectedRows ? Array.from(payload.selectedRows as Iterable<any>) : [];
  return rows.map((row: any) => Number(row?.id)).filter((id) => Number.isFinite(id));
}

/**
 * @param typeId the export type the list's records are (EXPORT_TYPE)
 * @param noun   the record, singular, as the list names it ("invoice", "TD")
 * @param eligibility optional; greys the entry out when no ticked row qualifies
 */
export function useCreateExportBatchAction(
  typeId: number,
  noun: string,
  eligibility?: ExportEligibility | null
): ActionMenuItem | null {
  const { canCreate } = useBatchExportGrants();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  return useMemo(() => {
    if (!canCreate) return null;
    return {
      name: "Create Export Batch",
      namePlural: "Create Export Batch",
      requiresSelection: true,
      multipleSelection: true,
      isReturnDataSelection: true,
      ...(eligibility
        ? {
            disabledPredicate: (rows: any[]) => rows.length > 0 && !rows.some(eligibility.isEligible),
            disabledReason: eligibility.reason,
          }
        : {}),
      onClick: async (payload: any) => {
        const ids = idsOf(payload);
        if (ids.length === 0) return;
        try {
          const { batch, stamped, refused } = await createBatchExportFromRecords(typeId, ids);
          enqueueSnackbar(`Export batch #${batch.id} created from ${plural(stamped.length, noun)}`, {
            variant: "success",
          });
          if (refused.length > 0) {
            enqueueSnackbar(`Left out ${plural(refused.length, noun)}: ${describeRefusals(refused)}`, {
              variant: "warning",
            });
          }
          // Router-relative (basename /app): the new batch, in its own module.
          navigate(`/batchexports/${batch.id}`);
        } catch (e) {
          if (e instanceof NothingToExportError) {
            enqueueSnackbar(`No export batch created: ${describeRefusals(e.refused)}`, { variant: "error" });
            return;
          }
          const detail = (e as any)?.response?.data?.detail;
          enqueueSnackbar(
            typeof detail === "string" ? detail : "Could not create the export batch",
            { variant: "error" }
          );
        }
      },
    };
  }, [canCreate, typeId, noun, eligibility, enqueueSnackbar, navigate]);
}
